import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRight, CheckCircle2, Award, ShieldCheck, Clock, FileCheck } from 'lucide-react'

const proofPoints = ['Boundary surveys', 'Topographic mapping', 'Construction setting out']

const trustBadges = [
  { icon: Award, label: 'SURCON Certified' },
  { icon: ShieldCheck, label: 'Licensed Surveyors' },
  { icon: FileCheck, label: 'ISO Compliant' },
  { icon: Clock, label: '24–72 hr Turnaround' },
]

export default function HeroSection() {
  return (
    <section className="relative min-h-[88vh] overflow-hidden bg-black pt-32 text-white">
      <img
        src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1800&auto=format&fit=crop&q=85"
        alt="Surveying team at an active construction site"
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(0,0,0,0.88)_0%,rgba(0,0,0,0.60)_45%,rgba(0,0,0,0.18)_100%)]" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/80 to-transparent" />

      <div className="relative z-10 section-padding mx-auto flex min-h-[calc(88vh-6rem)] max-w-7xl items-center py-16">
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
            className="mb-5 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.22em] text-accent"
          >
            <span className="h-px w-12 bg-accent" />
            Licensed geospatial consultants
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.7 }}
            className="max-w-4xl font-serif text-5xl font-semibold leading-[0.98] tracking-normal sm:text-6xl lg:text-7xl"
          >
            Advict Spatial Consult
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.22, duration: 0.6 }}
            className="mt-7 max-w-2xl text-base leading-8 text-neutral-200 sm:text-lg"
          >
            We provide reliable land surveying, mapping, and spatial advisory
            services for property, infrastructure, and development decisions
            that cannot afford uncertainty.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.34, duration: 0.6 }}
            className="mt-9 flex flex-col gap-3 sm:flex-row"
          >
            <Link to="/contact" className="btn-primary">
              Request a Survey
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/projects" className="btn-quiet-dark">
              View Project Work
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.46, duration: 0.6 }}
            className="mt-10 grid gap-3 border-l border-white/20 pl-5 text-sm text-neutral-200 sm:grid-cols-3 sm:border-l-0 sm:pl-0"
          >
            {proofPoints.map((point) => (
              <div key={point} className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-accent" />
                {point}
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      <div className="relative z-10 border-t border-white/15 bg-black/55 backdrop-blur">
        <div className="section-padding mx-auto max-w-7xl">
          <div className="grid grid-cols-2 divide-x divide-white/10 lg:grid-cols-4">
            {trustBadges.map((badge) => (
              <div key={badge.label} className="flex items-center gap-3 py-5 pr-4 sm:py-6">
                <badge.icon className="h-5 w-5 shrink-0 text-accent" />
                <div className="text-xs uppercase tracking-[0.18em] text-neutral-300">
                  {badge.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
